import { useCallback, useMemo } from 'react';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import { infoSrc } from '#shared/assets';
import { useConfirm } from '#shared/lib/hooks';
import { pluralize } from '#shared/lib/string';
import { constructorTestIds } from '#shared/lib/testids';
import { constructorProductLoggingEvents } from '../lib/constructorProductLoggingEvents';
import {
    CONSTRUCTOR_DEFAULT_MAX_MODULES,
    CONSTRUCTOR_DEFAULT_MAX_PAGES,
    CONSTRUCTOR_DEFAULT_MAX_SECTIONS,
} from '../model/constants';
import { getConstructorPagesSettings, getConstructorPreviewPage } from '../model/slices';

export type ConstructorLimitType = 'page' | 'section' | 'module';

const defaultLimits: Record<ConstructorLimitType, number> = {
    page: CONSTRUCTOR_DEFAULT_MAX_PAGES,
    section: CONSTRUCTOR_DEFAULT_MAX_SECTIONS,
    module: CONSTRUCTOR_DEFAULT_MAX_MODULES,
};

const getLimitTitle = (limit: ConstructorLimitType): string => {
    if (limit === 'page') {
        return 'Вы достигли лимита страниц';
    } else if (limit === 'section') {
        return 'Вы достигли лимита секций';
    } else if (limit === 'module') {
        return 'Вы достигли лимита модулей';
    }

    return 'Вы достигли лимита';
};

export const useConstructorLimits = (limitSettings: {
    pagesLimit?: number;
    sectionsLimit?: number;
    modulesLimit?: number;
}) => {
    const { confirm, renderConfirmModal } = useConfirm();

    const limits = useMemo((): Record<ConstructorLimitType, number> => {
        return {
            page: limitSettings.pagesLimit ?? defaultLimits.page,
            section: limitSettings.sectionsLimit ?? defaultLimits.section,
            module: limitSettings.modulesLimit ?? defaultLimits.module,
        };
    }, [limitSettings]);

    const getLimitBodyText = useCallback(
        (limit: ConstructorLimitType): string => {
            if (limit === 'page') {
                return `В редакторе можно создать до ${pluralize(limits.page, '%d страницы', '%d страниц', '%d страниц')}. Удалите ненужные страницы, чтобы добавить новые`;
            } else if (limit === 'section') {
                return `В редакторе можно создать до ${pluralize(limits.section, '%d секции', '%d секций', '%d секций')} на одной странице. Удалите ненужные секции, чтобы добавить новые`;
            } else if (limit === 'module') {
                return `В редакторе можно создать до ${pluralize(limits.module, '%d модуля', '%d модулей', '%d модулей')} в одной секции. Удалите ненужные модули, чтобы добавить новые`;
            }

            return 'Обратитесь, пожалуйста, в поддержку';
        },
        [limits]
    );

    const showLimit = useCallback(
        async (limit: ConstructorLimitType) => {
            logEventString(constructorProductLoggingEvents.LimitAchieved[limit], 'true');

            await confirm({
                imageSrc: infoSrc,
                title: getLimitTitle(limit),
                bodyText: getLimitBodyText(limit),
                applyText: 'Понятно',
                hideCancel: true,
                'data-testid': constructorTestIds.limitModal,
            });
        },
        [confirm, getLimitBodyText]
    );

    const checkForLimits = useCallback(
        (limit: ConstructorLimitType): ConstructorLimitType | null => {
            const pages = getConstructorPagesSettings();
            const currentPage = getConstructorPreviewPage();
            const allPages = Object.values(pages);

            if (limit === 'page') {
                if (allPages.length >= limits.page) {
                    return 'page';
                }
            } else if (limit === 'section') {
                const sections = pages[currentPage]?.layout;

                if (sections.length >= limits.section) {
                    return 'section';
                }
            }

            // Для module проверяем в конкретном месте, чтобы переполнение в одном месте не мешало другим местам
            return null;
        },
        [limits]
    );

    return {
        showLimit,
        checkForLimits,
        renderConfirmModal,
        limits,
    };
};
